import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { LoginFormData } from '@edytech/shared/validators';
import { loginSchema } from '@edytech/shared/validators';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { Mail, Lock, Globe, Share2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { signInWithGoogle, signInWithFacebook } from '../../lib/firebase';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
  onClose: () => void;
  onSwitchToRegister: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onClose, onSwitchToRegister }) => {
  const { login, socialLogin, error, clearError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setLocalError(null);
    clearError();
    try {
      await login(data.identifier, data.password);
      onClose();
      navigate('/dashboard');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setIsLoading(true);
    setLocalError(null);
    clearError();
    try {
      let result;
      if (provider === 'google') {
        result = await signInWithGoogle();
      } else {
        result = await signInWithFacebook();
      }
      // Get Firebase ID token
      const idToken = await result.user.getIdToken();
      await socialLogin(provider, idToken);
      onClose();
      navigate('/dashboard');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : `${provider} login failed`);
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = error || localError;

  return (
    <div>
      {displayError && (
        <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {displayError}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Email or Phone Number"
          type="text"
          placeholder="you@example.com or +233..."
          icon={<Mail className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
          {...register('identifier')}
          error={errors.identifier?.message}
        />
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          icon={<Lock className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
          {...register('password')}
          error={errors.password?.message}
          endAdornment={<button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
        />
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center space-x-2">
            <input type="checkbox" className="rounded border-[rgb(var(--border))]" />
            <span className="text-[rgb(var(--light-secondary))]">Remember me</span>
          </label>
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => {/* Open password reset modal */}}
          >
            Forgot password?
          </button>
        </div>
        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Log In'}
        </Button>
      </form>

      <div className="mt-4 flex items-center gap-4">
        <hr className="flex-1 border-[rgb(var(--border))]" />
        <span className="text-xs text-[rgb(var(--light-secondary))]">OR</span>
        <hr className="flex-1 border-[rgb(var(--border))]" />
      </div>

      <div className="mt-4 space-y-2">
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => handleSocialLogin('google')}
          disabled={isLoading}
        >
          <Globe className="mr-2 h-5 w-5" />
          Continue with Google
        </Button>
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => handleSocialLogin('facebook')}
          disabled={isLoading}
        >
          <Share2 className="mr-2 h-5 w-5" />
          Continue with Facebook
        </Button>
      </div>

      <p className="mt-4 text-center text-sm text-[rgb(var(--light-secondary))]">
        Don't have an account?{' '}
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={onSwitchToRegister}
        >
          Sign up
        </button>
      </p>
    </div>
  );
};